import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Copy, Check, Code2, ShoppingBag, Eye, ArrowRight, Terminal, BookOpen, Zap } from 'lucide-react';
import { toast } from 'sonner';

export default function DevelopersPage() {
  const navigate = useNavigate();
  const [copiedCode, setCopiedCode] = useState(null);

  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const loaderSnippet = `<script>
  (function(w, d, s, src, name) {
    if (w[name]) { return; }
    var ci = w[name] = function() {
      ci.q.push(arguments);
    };
    ci.q = [];
    var el = d.createElement(s);
    el.async = true;
    el.src = src;
    var firstScript = d.getElementsByTagName(s)[0];
    firstScript.parentNode.insertBefore(el, firstScript);
  })(window, document, 'script', 'https://cdn.commerceindex.com/ci.js', 'ci');

  // Initialize CI with your assigned brand ID
  ci('init', {
    brandId: 'YOUR_BRAND_ID'
  });
</script>`;

  const trackViewSnippet = `<script>
  ci('trackView', {
    productId: '{{ product.id }}',
    productSku: '{{ product.selected_or_first_available_variant.sku }}',
    productTitle: '{{ product.title }}',
    productUrl: '{{ shop.url }}{{ product.url }}'
  });
</script>`;

  const trackPurchaseSnippet = `<script>
  ci('trackPurchase', {
    orderId: '{{ order.id }}',
    currency: '{{ order.currency }}',
    items: [
      {% for line_item in order.line_items %}
      {
        productId: '{{ line_item.product.id }}',
        productSku: '{{ line_item.sku }}',
        productTitle: '{{ line_item.title }}',
        quantity: {{ line_item.quantity }},
        value: {{ line_item.price | money_without_currency }}
      }{% if forloop.last == false %},{% endif %}
      {% endfor %}
    ]
  });
</script>`;

  const apiExampleSearch = `POST /api/ci/search-enrich
Content-Type: application/json

{
  "query": "dyson v15 vacuum cleaner",
  "results": [
    {
      "title": "Dyson V15 Detect Cordless Vacuum",
      "url": "https://www.bestbuy.com/dyson-v15",
      "price": 699.99,
      "currency": "USD",
      "domain": "bestbuy.com"
    }
  ],
  "user_context": { "country": "US", "locale": "en-US" }
}`;

  const apiExampleEnrich = `POST /api/ci/product-enrich
Content-Type: application/json

{
  "context": {
    "domain": "amazon.com",
    "url": "https://www.amazon.com/dp/B08...",
    "country": "US"
  },
  "product": {
    "title": "Dyson V15 Detect Vacuum",
    "brand": "Dyson",
    "price": 699.99,
    "currency": "USD"
  }
}`;

  return (
    <div className="min-h-screen bg-[#0B1120]">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            background: 'radial-gradient(60% 60% at 50% 10%, rgba(62,241,199,0.18) 0%, rgba(62,241,199,0) 60%)'
          }}
        />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <Badge className="bg-[#3EF1C7]/20 text-[#3EF1C7] border-[#3EF1C7]/30 mb-6">
              <Code2 className="w-3 h-3 mr-1" />
              Developer Docs
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Integrate Commerce Index
              <span className="text-[#3EF1C7]"> in 30 Minutes</span>
            </h1>
            <p className="text-lg text-[#94A3B8] max-w-2xl mx-auto mb-8">
              Add social proof to your product pages. Show shoppers how many people are viewing 
              and purchasing your products in real-time.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button className="bg-[#3EF1C7] text-[#0B1120] hover:bg-[#3EF1C7]/90" data-testid="docs-get-started-btn">
                Get Your Brand ID
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button variant="outline" className="border-[#334155] text-[#E2E8F0] hover:bg-[#1E293B]" data-testid="docs-api-ref-btn" onClick={() => navigate('/docs/rest-api')}>
                <BookOpen className="w-4 h-4 mr-2" />
                Full API Reference
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* What You Get */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          <Card className="bg-[#0D1628] border-[#22304A]" data-testid="benefit-social-proof">
            <CardHeader>
              <Eye className="w-10 h-10 text-[#3EF1C7] mb-4" />
              <CardTitle className="text-white">Live Social Proof</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[#94A3B8] text-sm">
                Show "X people viewing now" and "Y purchases today" on your product pages
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0D1628] border-[#22304A]" data-testid="benefit-analytics">
            <CardHeader>
              <Zap className="w-10 h-10 text-[#7A5CFF] mb-4" />
              <CardTitle className="text-white">Product Analytics</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[#94A3B8] text-sm">
                Get insights into which products are trending and driving conversions
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0D1628] border-[#22304A]" data-testid="benefit-extension">
            <CardHeader>
              <ShoppingBag className="w-10 h-10 text-[#FFD166] mb-4" />
              <CardTitle className="text-white">Extension Display</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[#94A3B8] text-sm">
                Your social proof shows in our Chrome extension to millions of shoppers
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Integration Steps */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-white mb-8" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Quick Start Integration
          </h2>
          
          <Tabs defaultValue="step1" className="w-full">
            <TabsList className="bg-[#1E293B] border border-[#334155] mb-6">
              <TabsTrigger value="step1" className="data-[state=active]:bg-[#3EF1C7] data-[state=active]:text-[#0B1120]">
                1. Add Script
              </TabsTrigger>
              <TabsTrigger value="step2" className="data-[state=active]:bg-[#3EF1C7] data-[state=active]:text-[#0B1120]">
                2. Track Views
              </TabsTrigger>
              <TabsTrigger value="step3" className="data-[state=active]:bg-[#3EF1C7] data-[state=active]:text-[#0B1120]">
                3. Track Purchases
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="step1">
              <Card className="bg-[#0D1628] border-[#22304A]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Terminal className="w-5 h-5 text-[#3EF1C7]" />
                    Step 1: Add the CI Script Loader
                  </CardTitle>
                  <CardDescription className="text-[#94A3B8]">
                    Add this snippet to your global layout (before &lt;/body&gt;) so it loads on all relevant pages.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="relative">
                    <pre className="bg-[#0B1120] border border-[#334155] rounded-lg p-4 overflow-x-auto text-sm text-[#E2E8F0]">
                      <code>{loaderSnippet}</code>
                    </pre>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="absolute top-2 right-2 text-[#64748B] hover:text-white"
                      onClick={() => copyToClipboard(loaderSnippet, 'loader')}
                      data-testid="copy-loader-snippet"
                    >
                      {copiedCode === 'loader' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <div className="mt-4 p-4 bg-[#1E293B] rounded-lg border border-[#334155]">
                    <p className="text-sm text-[#FFD166]">
                      <strong>Note:</strong> We'll provide your <code className="bg-[#0B1120] px-1 rounded">YOUR_BRAND_ID</code> and 
                      the exact script URL during onboarding.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="step2">
              <Card className="bg-[#0D1628] border-[#22304A]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Eye className="w-5 h-5 text-[#3EF1C7]" />
                    Step 2: Track Product Views (PDP)
                  </CardTitle>
                  <CardDescription className="text-[#94A3B8]">
                    On each product detail page (PDP), call <code className="bg-[#0B1120] px-1 rounded">ci('trackView', ...)</code>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-[#94A3B8] mb-4">Example for Shopify Liquid:</p>
                  <div className="relative">
                    <pre className="bg-[#0B1120] border border-[#334155] rounded-lg p-4 overflow-x-auto text-sm text-[#E2E8F0]">
                      <code>{trackViewSnippet}</code>
                    </pre>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="absolute top-2 right-2 text-[#64748B] hover:text-white"
                      onClick={() => copyToClipboard(trackViewSnippet, 'trackView')}
                      data-testid="copy-trackview-snippet"
                    >
                      {copiedCode === 'trackView' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="step3">
              <Card className="bg-[#0D1628] border-[#22304A]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <ShoppingBag className="w-5 h-5 text-[#3EF1C7]" />
                    Step 3: Track Purchases
                  </CardTitle>
                  <CardDescription className="text-[#94A3B8]">
                    On order confirmation / "thank you" page, call <code className="bg-[#0B1120] px-1 rounded">ci('trackPurchase', ...)</code>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-[#94A3B8] mb-4">Example for Shopify order confirmation:</p>
                  <div className="relative">
                    <pre className="bg-[#0B1120] border border-[#334155] rounded-lg p-4 overflow-x-auto text-sm text-[#E2E8F0]">
                      <code>{trackPurchaseSnippet}</code>
                    </pre>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="absolute top-2 right-2 text-[#64748B] hover:text-white"
                      onClick={() => copyToClipboard(trackPurchaseSnippet, 'trackPurchase')}
                      data-testid="copy-trackpurchase-snippet"
                    >
                      {copiedCode === 'trackPurchase' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* API Reference */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-white mb-8" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            API Reference
          </h2>
          
          <div className="space-y-6">
            <Card className="bg-[#0D1628] border-[#22304A]" data-testid="api-search-enrich">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Badge className="bg-[#7A5CFF]/20 text-[#7A5CFF] border-[#7A5CFF]/30">POST</Badge>
                  <CardTitle className="text-white font-mono text-lg">/api/ci/search-enrich</CardTitle>
                </div>
                <CardDescription className="text-[#94A3B8]">
                  Process search results and return normalized price comparisons
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="relative">
                  <pre className="bg-[#0B1120] border border-[#334155] rounded-lg p-4 overflow-x-auto text-sm text-[#E2E8F0]">
                    <code>{apiExampleSearch}</code>
                  </pre>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-[#0D1628] border-[#22304A]" data-testid="api-product-enrich">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Badge className="bg-[#7A5CFF]/20 text-[#7A5CFF] border-[#7A5CFF]/30">POST</Badge>
                  <CardTitle className="text-white font-mono text-lg">/api/ci/product-enrich</CardTitle>
                </div>
                <CardDescription className="text-[#94A3B8]">
                  Process PDP context and return alternative prices + social proof
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="relative">
                  <pre className="bg-[#0B1120] border border-[#334155] rounded-lg p-4 overflow-x-auto text-sm text-[#E2E8F0]">
                    <code>{apiExampleEnrich}</code>
                  </pre>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-[#0D1628] border-[#22304A]" data-testid="api-product-proof">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Badge className="bg-[#3EF1C7]/20 text-[#3EF1C7] border-[#3EF1C7]/30">GET</Badge>
                  <CardTitle className="text-white font-mono text-lg">/api/ci/product-proof</CardTitle>
                </div>
                <CardDescription className="text-[#94A3B8]">
                  Get social proof data for a specific product
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="relative">
                  <pre className="bg-[#0B1120] border border-[#334155] rounded-lg p-4 overflow-x-auto text-sm text-[#E2E8F0]">
                    <code>{`GET /api/ci/product-proof?brand_id=xyz&product_id=abc123

Response:
{
  "brand_id": "xyz",
  "product_id": "abc123",
  "views_last_60_min": 37,
  "purchases_last_24h": 12,
  "generated_at": "2025-12-22T15:30:00Z"
}`}</code>
                  </pre>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Integration Checklist */}
        <Card className="bg-[#0D1628] border-[#22304A] mb-16" data-testid="integration-checklist">
          <CardHeader>
            <CardTitle className="text-white">Integration Checklist (Under 30 Minutes)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                'Insert the loader snippet with your brandId into your layout',
                'Add ci(\'trackView\', ...) to your PDP template',
                'Add ci(\'trackPurchase\', ...) to your order confirmation page',
                'Test locally: load PDP → verify product-view event in network tab',
                'Complete a test order → verify product-purchase event',
                'Notify Commerce Index team to confirm events are received'
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3 text-[#94A3B8]">
                  <div className="w-6 h-6 rounded-full bg-[#1E293B] border border-[#334155] flex items-center justify-center text-xs text-[#3EF1C7] font-medium flex-shrink-0">
                    {index + 1}
                  </div>
                  <span className="text-sm">{item}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Privacy Note */}
        <Card className="bg-[#1E293B] border-[#334155]" data-testid="privacy-note">
          <CardContent className="py-6">
            <h3 className="text-white font-semibold mb-2">Privacy & Data</h3>
            <ul className="text-sm text-[#94A3B8] space-y-1">
              <li>• We do <strong>not</strong> collect PII (no names, emails, addresses)</li>
              <li>• We use anonymous session IDs for deduplication and quality</li>
              <li>• Events are used to compute aggregate statistics and CI signals only</li>
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Footer CTA */}
      <div className="bg-[#0D1628] border-t border-[#22304A] py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-xl font-semibold text-white mb-4">Ready to integrate?</h3>
          <p className="text-[#94A3B8] mb-6">
            Contact us to get your Brand ID and API key to start collecting social proof data.
          </p>
          <Button className="bg-[#3EF1C7] text-[#0B1120] hover:bg-[#3EF1C7]/90" data-testid="footer-contact-btn">
            Contact Integration Team
          </Button>
        </div>
      </div>
    </div>
  );
}
